import React from 'react';
import radium from 'radium';
import {ThemeContext} from '../theme/Theme.jsx'
import './FormElements.scss';
import Select, {ActionMeta, SingleValue} from 'react-select'

interface LeoSelectProps {
    name: string,
    id: string,
    labeledById?: string,
    value?: any,
    onChange?: any,
    disabled?: boolean,
    error?: boolean,
    children?: React.ReactNode,
    placeholder?: string,
    valueInput?: string,
    options?: { value: any, label: string, isSelected?: boolean, isDisabled?: boolean }[],
    errors?: { error: any, message: string }[],
    className?: string,
    centerText?: boolean,
    smallDropdown?: boolean,
    isClearable?: boolean,

}

const defaultProps = {
    disabled: false,
    placeholder: 'Bitte auswählen',
    value: '',
};


function LeoSelect(propsIn: LeoSelectProps) {
    const props = {...defaultProps, ...propsIn};
    const context = React.useContext(ThemeContext);

    const formElementStyle = {
        background: context.themeManager.value('form-element', 'background', 'bg-color-form-element'),
        borderColor: context.themeManager.value('form-element', 'borderColor', 'border-color-form-element'),
        color: context.themeManager.value('form-element', 'color', 'fg-color-form-element'),
        outlineColor: context.themeManager.value('form-element', 'outlineColor', 'border-color-form-element-focus'),
        placeholderColor: context.themeManager.valueOrDefault('form-element', 'placeholderColor', null, '#777575'),
    };

    const dropdownStyle = {
        background: context.themeManager.valueOrDefault('form-select', 'menuBackground', 'bg-color-form-element', '#fff'),
        color: context.themeManager.valueOrDefault('form-select', 'menuColor', 'fg-color-form-element', '#333'),
        selectedColor: context.themeManager.valueOrDefault('form-select', 'option-selected-color', 'fg-color-form-element', '#333'),
        separatorColor: context.themeManager.valueOrDefault('form-select', 'separatorColor', 'border-color-form-element', '#9E9E9E'),
        iconColor: context.themeManager.valueOrDefault('form-select', 'iconColor', 'border-color-form-element', '#9E9E9E'),
    };

    const style: any = {
        select: {
            background: formElementStyle.background,
            borderColor: formElementStyle.borderColor,
            color: formElementStyle.color,
            ':focus': {
                borderColor: formElementStyle.outlineColor,
            },
        },
        placeholder: {
            color: formElementStyle.placeholderColor,
        },
        control: {
            background: formElementStyle.background,
            borderColor: formElementStyle.borderColor,
            outlineColor: formElementStyle.outlineColor,
        },
        input: {
            color: formElementStyle.color,
        },
        scrollbar: {
            thumbColor: context.themeManager.valueOrDefault(
                'form-select',
                'scrollbar-thumb-color',
                null,
                formElementStyle.color
            ),
            thumbHoverColor: context.themeManager.valueOrDefault(
                'form-select',
                'scrollbar-thumb-hover-color',
                null,
                formElementStyle.outlineColor
            ),
            trackColor: context.themeManager.valueOrDefault(
                'form-select',
                'scrollbar-track-color',
                null,
                dropdownStyle.background
            ),
        },
        option: {
            selected: {
                backgroundColor: context.themeManager.valueOrDefault('form-select', 'option-selected-bg-color', null, 'rgba(0,0,0,.08)'),
                color: dropdownStyle.selectedColor,
            },
            focused: {
                backgroundColor: context.themeManager.valueOrDefault('form-select', 'option-focused-bg-color', null, 'rgba(0,0,0,.05)'),
                color: context.themeManager.valueOrDefault('form-select', 'option-focused-color', null, dropdownStyle.selectedColor),
            }
        },
    };

    const errorMsgStyle = {
        color: context.themeManager.valueOrDefault('form-element-errorMsg', 'color', 'color-error', '#e10101'),
        fontWeight: context.themeManager.valueOrDefault('form-element-errorMsg', 'fontWeight', null, '600'),
        padding: '.3em 0 0 0'
    }

    const errStyle: any = {
        control: {
            background: formElementStyle.background,
            borderColor: context.themeManager.valueOrDefault('form-element-errorMsg', 'borderColor', 'color-error', '#e10101'),
            color: formElementStyle.color,
        },
        input: {
            color: formElementStyle.color,
        },
    };

    const handleNativeChange = ((newValue: SingleValue<{ value: any; label: string; }>, actionMeta: ActionMeta<{
        value: any;
        label: string;
    }>) => {
        const event: any = {
            target: {
                name: actionMeta.name,
                value: newValue?.value,
            }
        }
        props.onChange(event)
    })

    const targetHeight = props.smallDropdown ? 25 : 40;

    return (
        <div className="leo-select__container">
            <Select
                styles={{
                    container: (baseStyles) => ({
                        ...baseStyles,
                        ':focus-visible': {
                            outlineColor: props.errors ? errStyle.control.borderColor : style.control.outlineColor,
                        },
                    }),
                    control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: props.errors ? errStyle.control.borderColor : style.control.borderColor,
                        background: props.errors ? errStyle.control.background : style.control.background,
                        textAlign: 'center',
                        borderRadius: 0,
                        boxShadow: 'none',
                        outline: state.isFocused ? 'auto' : 'none',
                        outlineColor: props.errors ? errStyle.control.borderColor : style.control.outlineColor,
                        paddingLeft: !props.centerText ? "2.8em" : "1.5em",
                        ':hover': {
                            borderColor: props.errors ? errStyle.control.borderColor : style.control.borderColor,
                        },
                        minHeight: 'initial',
                    }),
                    valueContainer: (base: any) => ({
                        ...base,
                        height: `${targetHeight - 1 - 1}px`,
                        padding: '0 8px',
                    }),
                    clearIndicator: (base: any) => ({
                        ...base,
                        padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
                        color: props.errors ? errStyle.input.color : dropdownStyle.iconColor,
                        ':hover': {
                            color: props.errors ? errStyle.input.color : dropdownStyle.iconColor,
                        },
                    }),
                    dropdownIndicator: (base: any) => ({
                        ...base,
                        padding: `${(targetHeight - 20 - 1 - 1) / 2}px`,
                        color: props.errors ? errStyle.input.color : dropdownStyle.iconColor,
                        ':hover': {
                            color: props.errors ? errStyle.input.color : dropdownStyle.iconColor,
                        },
                    }),
                    indicatorSeparator: (baseStyles) => ({
                        ...baseStyles,
                        backgroundColor: props.errors ? errStyle.input.color : dropdownStyle.separatorColor,
                    }),
                    input: (baseStyles) => ({
                        ...baseStyles,
                        color: props.errors ? errStyle.input.color : style.input.color,
                    }),
                    singleValue: (baseStyles) => ({
                        ...baseStyles,
                        color: props.errors ? errStyle.input.color : style.input.color,
                    }),
                    placeholder: (baseStyles) => ({
                        ...baseStyles,
                        color: style.placeholder.color,
                        fontStyle: 'italic',
                    }),
                    menu: (baseStyles) => ({
                        ...baseStyles,
                        marginTop: '0',
                        borderRadius: '0',
                        background: dropdownStyle.background,
                        border: `1px solid ${props.errors ? errStyle.control.borderColor : style.control.borderColor}`,
                        boxShadow: 'none',
                        overflow: 'hidden',
                    }),
                    menuList: (baseStyles) => ({
                        ...baseStyles,
                        paddingTop: '0',
                        paddingBottom: '0',
                        background: dropdownStyle.background,

                        scrollbarColor: `${style.scrollbar.thumbColor} ${style.scrollbar.trackColor}`,
                        scrollbarWidth: 'thin',

                        '::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '::-webkit-scrollbar-track': {
                            background: style.scrollbar.trackColor,
                        },
                        '::-webkit-scrollbar-thumb': {
                            background: style.scrollbar.thumbColor,
                        },
                        '::-webkit-scrollbar-thumb:hover': {
                            background: style.scrollbar.thumbHoverColor,
                        },
                    }),
                    option: (baseStyles,state) => ({
                        ...baseStyles,
                        backgroundColor: state.isFocused ?
                            style.option.focused.backgroundColor : state.isSelected ?
                                style.option.selected.backgroundColor :
                                dropdownStyle.background,
                        color: state.isFocused ?
                            style.option.focused.color : state.isSelected ?
                                style.option.selected.color :
                                dropdownStyle.color,
                        ':active': {
                            ...baseStyles[':active'],
                            backgroundColor: style.option.focused.backgroundColor,
                            color: style.option.focused.color,
                        }
                    })
                }}
                placeholder={props.placeholder}
                inputId={props.id}
                name={props.name}
                isDisabled={props.disabled}
                options={props.options}
                {...(props.labeledById ? {'aria-labelledby': props.labeledById} : {})}
                aria-invalid={!!props.errors}
                aria-errormessage={"error_" + props.id}
                isSearchable={false}
                onChange={handleNativeChange}
                value={props.options?.filter(option => option.value == props.value)}
                className={props?.className}
                isClearable={props?.isClearable}
            >
            </Select>
            {props.errors &&
                <div id={"error_" + props.id} style={errorMsgStyle}>{
                    props.errors.map((error) => error.message)}
                </div>
            }
        </div>
    );
}

export default radium(LeoSelect);
